/**
 * CallViewModal - Модальное окно просмотра звонка
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  Divider,
  Box,
  Button,
} from '@mantine/core';
import {
  IconPhone,
  IconUser,
  IconClock,
  IconMessage,
  IconPencil,
  IconBriefcase,
  IconCalendar,
  IconEdit,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  if (!contact) return 'Неизвестный контакт';
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (!user) return 'Не указан';
  if (user.full_name) return user.full_name;
  return user.email;
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получение инициалов контакта
 */
const getInitials = (contact) => {
  if (!contact) return 'К';
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'К';
};

/**
 * Компонент для отображения поля
 */
const InfoField = ({ icon: Icon, label, value, children }) => {
  if (!value && !children) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {children || <Text size="sm">{value}</Text>}
      </Box>
    </Group>
  );
};

const CallViewModal = ({
  opened,
  onClose,
  call,
  onEdit,
}) => {
  if (!call) return null;

  const contactName = formatContactName(call.contact);
  const callerName = formatUserName(call.caller);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Звонок"
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        {/* Заголовок с аватаром контакта */}
        <Group>
          <Avatar size="xl" radius="xl" color="green">
            {getInitials(call.contact)}
          </Avatar>
          <Box style={{ flex: 1 }}>
            <Group gap="xs">
              <Text size="lg" fw={600}>
                {contactName}
              </Text>
              <Badge size="sm" variant="filled" radius="xl" color="green" leftSection={<IconPhone size={10} />}>
                Звонок
              </Badge>
              {call.is_edited && (
                <Badge size="sm" variant="filled" radius="xl" color="yellow" leftSection={<IconEdit size={10} />}>
                  Изменено
                </Badge>
              )}
            </Group>
            {call.contact?.position && (
              <Group gap={4} mt={4}>
                <IconBriefcase size={14} color={tokens.colors.neutral[6]} />
                <Text size="sm" c="dimmed">
                  {call.contact.position}
                </Text>
              </Group>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Информация о звонке */}
        <Stack gap="md">
          {/* Тема */}
          {call.subject && (
            <InfoField icon={IconMessage} label="Тема звонка">
              <Text size="sm" fw={500}>{call.subject}</Text>
            </InfoField>
          )}

          {/* Дата и время */}
          <InfoField
            icon={IconCalendar}
            label="Дата и время"
            value={formatDateTime(call.call_datetime)}
          />

          {/* Кто звонил */}
          <InfoField icon={IconUser} label="Кто звонил" value={callerName} />
        </Stack>

        {/* Комментарий */}
        {call.comment && (
          <>
            <Divider />
            <Stack gap="sm">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Комментарий
              </Text>
              <Box
                p="md"
                style={{
                  backgroundColor: tokens.colors.neutral[1],
                  borderRadius: '8px',
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {call.comment}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        {/* Причина редактирования */}
        {call.is_edited && call.edit_reason && (
          <>
            <Divider />
            <Stack gap="sm">
              <Group gap="xs">
                <IconEdit size={14} color={tokens.colors.semantic.warning.primary} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Причина изменения
                </Text>
              </Group>
              <Box
                p="md"
                style={{
                  backgroundColor: '#FFF8E1',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${tokens.colors.semantic.warning.primary}`,
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {call.edit_reason}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => {
              onClose();
              onEdit(call);
            }}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CallViewModal;
